/**
 * Account activity — every time an administrator viewed the portal as you (ruling 3, Isaiah Cotto, 2026-10-26: the
 * person is told). Administrator by first name and last initial, their reason, when, and how it ended.
 * GET /account/sign-in-as. Read-only.
 */
import React, { useEffect, useState } from 'react';
import { Eye } from 'lucide-react';
import { apiRequest } from '../services/api';

export interface MySignInAsRecord {
  id: string;
  startedAt: string;
  expiresAt: string;
  endedAt: string | null;
  status: 'ACTIVE' | 'EXPIRED' | 'EXITED' | 'REVOKED';
  reason: string;
  administrator: string;
}

const WHEN = new Intl.DateTimeFormat('en-US', {
  timeZone: 'America/New_York', month: 'long', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit',
});
const HOW: Record<MySignInAsRecord['status'], string> = {
  ACTIVE: 'In progress now',
  EXPIRED: 'Ended after its 20 minutes',
  EXITED: 'Ended by the administrator',
  REVOKED: 'Ended by the system',
};

export default function AccountActivity() {
  const [items, setItems] = useState<MySignInAsRecord[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    apiRequest<{ items: MySignInAsRecord[] }>('GET', '/account/sign-in-as').then((r) => {
      if (!live) return;
      if (r.success && r.data) setItems((r.data as { items: MySignInAsRecord[] }).items);
      else setError(r.status === undefined ? 'The server could not be reached.' : r.message || 'Your account activity could not be loaded.');
    });
    return () => { live = false; };
  }, []);

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8" style={{ color: 'var(--tide-ink, #10252d)' }}>
      <h1 className="text-2xl font-semibold">Account activity</h1>
      <p className="mt-1 text-sm" style={{ color: 'var(--tide-ink-2, #4f646b)' }}>
        When an administrator views the portal as you, it is read-only: they cannot change, send or sign anything. Each
        time is listed here with the reason they gave.
      </p>
      {error && <p role="alert" className="mt-4 text-sm" style={{ color: '#9b2c1a' }}>{error}</p>}
      {items && items.length === 0 && (
        <p className="mt-6 text-sm" style={{ color: 'var(--tide-ink-3, #586c73)' }}>No administrator has viewed the portal as you.</p>
      )}
      {items && items.length > 0 && (
        <ol className="mt-6 space-y-3">
          {items.map((h) => (
            <li key={h.id} className="rounded-lg border bg-white p-3 text-sm" style={{ borderColor: 'var(--tide-line, #d7e5e1)' }}>
              <div className="flex items-center gap-2 font-medium">
                <Eye className="h-4 w-4" aria-hidden="true" style={{ color: 'var(--tide-brand, #17685d)' }} />
                {h.administrator} viewed the portal as you
              </div>
              <div className="mt-0.5" style={{ color: 'var(--tide-ink-2, #4f646b)' }}>
                {WHEN.format(new Date(h.startedAt))} ET · {HOW[h.status]}
              </div>
              <div className="mt-1 break-words">Reason: {h.reason}</div>
            </li>
          ))}
        </ol>
      )}
    </main>
  );
}
